// Setup key of an authenticator (first enrollment and D30 regeneration): a QR code of the otpauth:// URI (what password
// managers such as 1Password scan), the base32 secret in groups of four for manual entry and the otpauth:// link that
// opens an authenticator app. Shown once; never stored in the browser. The QR is computed locally (uqr), never fetched.
import { useMemo } from "react";
import { encode } from "uqr";
import { useI18n } from "../../i18n/index.tsx";
import type { MfaEnrollment } from "../../api/types.ts";

/** QR modules of a text, with a quiet zone of 4 modules (ISO/IEC 18004). */
export function qrModules(text: string): boolean[][] {
  return encode(text, { ecc: "M", border: 4 }).data;
}

/** One SVG path for every dark module: crisp at any size, no injected markup. */
export function qrPath(modules: readonly (readonly boolean[])[]): string {
  let d = "";
  modules.forEach((row, y) => row.forEach((dark, x) => (dark ? (d += `M${x} ${y}h1v1h-1z`) : undefined)));
  return d;
}

function QrCode({ text, label }: { text: string; label: string }) {
  const modules = useMemo(() => qrModules(text), [text]);
  const size = modules.length;
  return (
    <svg className="mfa-qr" data-testid="mfa-qr" role="img" aria-label={label} viewBox={`0 0 ${size} ${size}`} width={208} height={208} shapeRendering="crispEdges">
      <rect width={size} height={size} fill="#ffffff" />
      <path d={qrPath(modules)} fill="#000000" />
    </svg>
  );
}

export function MfaSecret({ enrollment }: { enrollment: MfaEnrollment }) {
  const { t } = useI18n();
  return (
    <div className="mfa-secret">
      {enrollment.otpauth_uri ? (
        <>
          <span className="field-hint">{t("mfa.qrLabel")}</span>
          <QrCode text={enrollment.otpauth_uri} label={t("mfa.qrAlt")} />
        </>
      ) : null}
      <span className="field-hint">{t("mfa.secretLabel")}</span>
      <code className="mono" data-testid="mfa-secret">
        {enrollment.secret.replace(/(.{4})/g, "$1 ").trim()}
      </code>
      {enrollment.otpauth_uri ? (
        <a href={enrollment.otpauth_uri} className="field-hint">
          {t("mfa.openAuthenticator")}
        </a>
      ) : null}
    </div>
  );
}
